import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, asset } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Leaf, Beef, FolderPlus, Sparkles, Loader2 } from "lucide-react";

const EMPTY = { name: "", category_id: "", price: "", price_takeaway: "", price_delivery: "", description: "", image: "", is_veg: true, tax_rate: 5, available: true, recipe: [], addons: [], packaging: "", brand_id: "" };

export default function MenuManage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catName, setCatName] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [activeCat, setActiveCat] = useState("all");
  const [genId, setGenId] = useState(null);

  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: menu = [] } = useQuery({ queryKey: ["menu"], queryFn: async () => (await api.get("/menu")).data });
  const { data: inventory = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: async () => (await api.get("/brands")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, category_id: cats[0]?.id || "" }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m, price: m.price, tax_rate: m.tax_rate }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.category_id) return toast.error("Name & category required");
    const body = {
      ...form,
      price: Number(form.price),
      price_takeaway: Number(form.price_takeaway || 0),
      price_delivery: Number(form.price_delivery || 0),
      tax_rate: Number(form.tax_rate),
      recipe: form.recipe || [],
      addons: (form.addons || []).filter((a) => a.name),
    };
    if (editing) await api.put(`/menu/${editing.id}`, body);
    else await api.post("/menu", body);
    toast.success(editing ? "Item updated" : "Item added"); setOpen(false);
    qc.invalidateQueries({ queryKey: ["menu"] });
  };
  const del = async (m) => { await api.delete(`/menu/${m.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["menu"] }); };
  const addCat = async () => {
    if (!catName) return;
    await api.post("/categories", { name: catName, sort: cats.length + 1 });
    toast.success("Category added"); setCatName(""); setCatOpen(false);
    qc.invalidateQueries({ queryKey: ["categories"] });
  };

  const list = menu.filter((m) => activeCat === "all" || m.category_id === activeCat);
  const missingPhotos = menu.filter((m) => !m.image).length;

  const generatePhoto = async (m) => {
    setGenId(m.id);
    try {
      await api.post(`/menu/${m.id}/generate-photo`);
      toast.success(`Generated a photo for ${m.name}`);
      qc.invalidateQueries({ queryKey: ["menu"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo generation failed");
    } finally { setGenId(null); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Menu" subtitle={`${menu.length} items · ${cats.length} categories${missingPhotos ? ` · ${missingPhotos} missing photos` : ""}`}>
        <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="add-cat-btn"><FolderPlus className="w-4 h-4 mr-1" /> Category</Button>
        <Button onClick={openNew} data-testid="add-menu-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
      </PageHeader>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        <button onClick={() => setActiveCat("all")} className={`px-3.5 py-1.5 rounded-full text-sm border ${activeCat === "all" ? "bg-[var(--kul-charcoal)] text-white" : "bg-white"}`}>All</button>
        {cats.map((c) => <button key={c.id} onClick={() => setActiveCat(c.id)} className={`px-3.5 py-1.5 rounded-full text-sm border whitespace-nowrap ${activeCat === c.id ? "bg-[var(--kul-charcoal)] text-white" : "bg-white"}`}>{c.name}</button>)}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {list.map((m) => (
          <div key={m.id} className="kul-card overflow-hidden group" data-testid={`menu-row-${m.id}`}>
            <div className="h-32 bg-neutral-100 relative">
              {m.image ? <img src={asset(m.image)} alt={m.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">No photo</div>}
              {!m.available && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-sm">Unavailable</div>}
              <span className="absolute top-2 left-2">{m.is_veg ? <Badge className="bg-green-100 text-green-700 gap-1"><Leaf className="w-3 h-3" />Veg</Badge> : <Badge className="bg-red-100 text-red-700 gap-1"><Beef className="w-3 h-3" />Non-veg</Badge>}</span>
              <button onClick={() => generatePhoto(m)} disabled={genId === m.id} data-testid={`gen-photo-${m.id}`}
                title="Generate AI photo"
                className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium glass text-[var(--kul-primary)] shadow-sm active:scale-95 transition-transform disabled:opacity-60">
                {genId === m.id ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating…</> : <><Sparkles className="w-3 h-3" /> {m.image ? "Regenerate" : "AI Photo"}</>}
              </button>
            </div>
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="font-medium truncate">{m.name}</div><div className="text-xs text-neutral-400">{catMap[m.category_id] || "—"}</div></div>
                <span className="font-mono-k font-semibold text-[var(--kul-primary)]">{money(m.price, sym)}</span>
              </div>
              <div className="flex gap-1 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(m)} data-testid={`edit-${m.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" className="text-[var(--kul-danger)]" onClick={() => del(m)} data-testid={`del-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{editing ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="menu-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="menu-cat">
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Dine-in ({sym})</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="menu-price" /></div>
              <div><Label>Takeaway</Label><Input type="number" value={form.price_takeaway || ""} onChange={(e) => setForm({ ...form, price_takeaway: e.target.value })} /></div>
              <div><Label>Delivery / online</Label><Input type="number" value={form.price_delivery || ""} onChange={(e) => setForm({ ...form, price_delivery: e.target.value })} /></div>
            </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>GST %</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="menu-tax" /></div>
              <div><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cloud brand</Label>
                <select className="w-full h-10 rounded-lg border px-2 text-sm" value={form.brand_id || ""} onChange={(e) => setForm({ ...form, brand_id: e.target.value })}>
                  <option value="">None (dine-in)</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Packaging / prep note</Label>
                <Input value={form.packaging || ""} onChange={(e) => setForm({ ...form, packaging: e.target.value })} placeholder="Box + sauce sachet" data-testid="menu-packaging" />
              </div>
            </div>
            <div>
              <Label>Add-ons (name, extra ₹)</Label>
              {(form.addons || []).map((a, i) => (
                <div key={i} className="flex gap-2 mt-1">
                  <Input value={a.name} placeholder="Extra cheese" onChange={(e) => {
                    const addons = [...(form.addons || [])]; addons[i] = { ...addons[i], name: e.target.value }; setForm({ ...form, addons });
                  }} />
                  <Input type="number" className="w-24" value={a.price} onChange={(e) => {
                    const addons = [...(form.addons || [])]; addons[i] = { ...addons[i], price: e.target.value }; setForm({ ...form, addons });
                  }} />
                </div>
              ))}
              <button type="button" className="text-xs text-[var(--kul-primary)] mt-1" onClick={() => setForm({ ...form, addons: [...(form.addons || []), { name: "", price: 0 }] })}>+ add-on</button>
            </div>
            <div>
              <Label>Recipe (inventory deduction on pay)</Label>
              {(form.recipe || []).map((r, i) => (
                <div key={i} className="flex gap-2 mt-1">
                  <select className="flex-1 h-10 rounded-lg border px-2 text-sm" value={r.inventory_id || ""} onChange={(e) => {
                    const recipe = [...(form.recipe || [])]; recipe[i] = { ...recipe[i], inventory_id: e.target.value }; setForm({ ...form, recipe });
                  }}>
                    <option value="">Ingredient</option>
                    {inventory.map((ing) => <option key={ing.id} value={ing.id}>{ing.name}</option>)}
                  </select>
                  <Input type="number" className="w-24" placeholder="qty" value={r.qty || ""} onChange={(e) => {
                    const recipe = [...(form.recipe || [])]; recipe[i] = { ...recipe[i], qty: Number(e.target.value) }; setForm({ ...form, recipe });
                  }} />
                </div>
              ))}
              <button type="button" className="text-xs text-[var(--kul-primary)] mt-1" onClick={() => setForm({ ...form, recipe: [...(form.recipe || []), { inventory_id: "", qty: 1 }] })}>+ ingredient</button>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_veg} onCheckedChange={(v) => setForm({ ...form, is_veg: v })} data-testid="menu-veg" /> Vegetarian</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="menu-avail" /> Available</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-menu" className="bg-[var(--kul-primary)] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>New Category</DialogTitle></DialogHeader>
          <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="e.g. Combos" data-testid="cat-name" />
          <DialogFooter><Button onClick={addCat} className="bg-[var(--kul-primary)] text-white" data-testid="save-cat">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
